import * as React from 'react';
import {
    autoPlacement,
    autoUpdate,
    flip,
    Middleware,
    offset,
    OpenChangeReason,
    shift,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
    useTransitionStatus,
} from '@floating-ui/react';

import type { PopoverProps, PopoverAnimationStyle } from './Popover.types';
import { useHoneycombTheme } from '../../theming';
import { useCulture } from '../../culture';

function mapPlacementForRTL(placement: PopoverProps['placement'], isRTL: boolean): PopoverProps['placement'] {
    if (!isRTL) return placement;

    switch (placement) {
        case 'left':
            return 'right';
        case 'left-start':
            return 'right-start';
        case 'left-end':
            return 'right-end';
        case 'right':
            return 'left';
        case 'right-start':
            return 'left-start';
        case 'right-end':
            return 'left-end';
        default:
            return placement; // top / bottom variants stay the same
    }
}

export function usePopover(props: Omit<PopoverProps, 'children'>) {
    const {
        open: openProp,
        defaultOpen = false,
        onOpenChange,
        onClose,
        role: roleProp = 'dialog',
        dismiss: dismissProp = false,
        click: clickProp = false,
        placement = 'bottom',
        offset: offsetProp = 4,
        flip: flipProp = false,
        shift: shiftProp = false,
        autoPlacement: autoPlacementProp = false,
        disablePortal = false,
        onCalculateSpace,
        fullWidth = false,
        animationStyle: animationStyleProp,
    } = props;

    const isControlled = openProp !== undefined;
    const [openState, setOpenState] = React.useState(defaultOpen);

    const { theme } = useHoneycombTheme();
    const { dir } = useCulture();
    const isRTL = dir === 'rtl';

    const animationStyle: PopoverAnimationStyle = animationStyleProp ?? 'scale-fade';

    const animationEnabled = animationStyle !== 'none';

    // Map placement for RTL
    const logicalPlacement = React.useMemo(() => mapPlacementForRTL(placement, isRTL), [placement, isRTL]);

    const getOpen = React.useCallback(() => {
        return isControlled ? openProp : openState;
    }, [isControlled, openProp, openState]);

    const handleOpenChange = React.useCallback(
        (newOpen: boolean, event?: Event, reason?: OpenChangeReason) => {
            if (!isControlled) {
                setOpenState(newOpen);
            }

            onOpenChange?.(newOpen, event, reason);

            if (!newOpen) {
                onClose?.(event, reason);
            }
        },
        [isControlled, onClose, onOpenChange]
    );

    // Compose middleware based on props
    const middleware = React.useMemo((): Array<Middleware> => {
        const array: Array<Middleware> = [];

        if (offsetProp) array.push(offset(offsetProp));
        if (flipProp) array.push(flip());
        if (shiftProp) array.push(shift());
        if (autoPlacementProp) array.push(autoPlacement());

        if (onCalculateSpace) {
            array.push(
                size({
                    apply(params) {
                        onCalculateSpace(params.availableWidth, params.availableHeight);
                    },
                })
            );
        }

        return array;
    }, [autoPlacementProp, flipProp, offsetProp, onCalculateSpace, shiftProp]);

    const data = useFloating({
        open: getOpen(),
        onOpenChange: handleOpenChange,
        middleware,
        placement: logicalPlacement,
        strategy: fullWidth ? 'fixed' : 'absolute',
        transform: !fullWidth,
        whileElementsMounted: autoUpdate,
    });

    const { context } = data;

    const transition = useTransitionStatus(context, {
        duration: theme.components.themes.Popover.animationDuration,
    });

    const isMounted = animationEnabled ? transition.isMounted : context.open;

    const transitionStatus = animationEnabled ? transition.status : 'open';

    const role = useRole(context, { role: roleProp });
    const dismiss = useDismiss(context, { enabled: dismissProp });
    const click = useClick(context, { enabled: clickProp });

    const interactions = useInteractions([role, dismiss, click]);

    return React.useMemo(
        () => ({
            open: getOpen(),
            disablePortal,
            ...data,
            ...interactions,
            isMounted,
            transitionStatus,
            fullWidth,
            animationStyle,
        }),
        [getOpen, disablePortal, data, interactions, isMounted, transitionStatus, fullWidth, animationStyle]
    );
}
